import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { EventService } from '../services/APIService';

// Async thunks for API calls
export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EventService.getAllEvents();
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const fetchEventById = createAsyncThunk(
  'events/fetchEventById',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await EventService.getEvent(eventId);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await EventService.createEvent(eventData);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ eventId, eventData }, { rejectWithValue }) => {
    try {
      const response = await EventService.updateEvent(eventId, eventData);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      await EventService.deleteEvent(eventId);
      return eventId;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const registerForEvent = createAsyncThunk(
  'events/registerForEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await EventService.registerForEvent(eventId);
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

export const fetchMyEvents = createAsyncThunk(
  'events/fetchMyEvents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await EventService.getMyEvents();
      return response;
    } catch (error) {
      return rejectWithValue(error.toString());
    }
  }
);

const eventSlice = createSlice({
  name: 'events',
  initialState: {
    events: [],
    currentEvent: null,
    myEvents: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchEvents
      .addCase(fetchEvents.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Handle the API response - could be array or object with data property
        if (Array.isArray(action.payload)) {
          state.events = action.payload;
        } else if (action.payload && action.payload.data && Array.isArray(action.payload.data)) {
          state.events = action.payload.data;
        } else {
          state.events = [];
          console.error('Unexpected format in fetchEvents response:', action.payload);
        }
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle fetchEventById
      .addCase(fetchEventById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchEventById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Handle the API response format - could be direct object or object with data property
        if (action.payload && action.payload.data) {
          state.currentEvent = action.payload.data;
        } else if (action.payload && typeof action.payload === 'object') {
          state.currentEvent = action.payload;
        } else {
          state.currentEvent = null;
          console.error('Unexpected format in fetchEventById response:', action.payload);
        }
      })
      .addCase(fetchEventById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle createEvent
      .addCase(createEvent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.events.push(action.payload);
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle updateEvent
      .addCase(updateEvent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.events.findIndex((event) => event.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        state.currentEvent = action.payload;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle deleteEvent
      .addCase(deleteEvent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.events = state.events.filter((event) => event.id !== action.payload);
        if (state.currentEvent && state.currentEvent.id === action.payload) {
          state.currentEvent = null;
        }
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle registerForEvent
      .addCase(registerForEvent.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(registerForEvent.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Update the event in the state if needed
      })
      .addCase(registerForEvent.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Handle fetchMyEvents
      .addCase(fetchMyEvents.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyEvents.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.myEvents = action.payload;
      })
      .addCase(fetchMyEvents.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { clearCurrentEvent } = eventSlice.actions;

// Selectors
export const selectAllEvents = (state) => state.events.events;
export const selectEventById = (state, eventId) =>
  state.events.events.find((event) => event.id === eventId);
export const selectCurrentEvent = (state) => state.events.currentEvent;
export const selectMyEvents = (state) => state.events.myEvents;
export const selectEventsStatus = (state) => state.events.status;
export const selectEventsError = (state) => state.events.error;

export default eventSlice.reducer;
